import { useMemo } from "react";
import { Form, type FormInstance } from "antd";
import {
  getParcelCreateStepAccess,
  isParcelAddressStepComplete,
  isParcelNameStepComplete,
  isParcelPackagingStepComplete,
  type ParcelCreateStepAccess,
  type ParcelCreateStepHints,
} from "../parcelCreateSteps";
import { useParcelPackagingSelection } from "./useParcelPackagingSelection";

export type ParcelCreateSteps = {
  enabled: ParcelCreateStepAccess;
  hints: ParcelCreateStepHints;
  productsComplete: boolean;
  packagingComplete: boolean;
  addressComplete: boolean;
};

export function useParcelCreateSteps(
  form: FormInstance,
  hasMerchant?: boolean
): ParcelCreateSteps {
  const packaging = useParcelPackagingSelection(form);
  const destination = Form.useWatch("destination", form);
  const parcelName = Form.useWatch("parcel_name", form);

  const productsComplete = isParcelNameStepComplete(parcelName);

  const packagingComplete = useMemo(
    () =>
      isParcelPackagingStepComplete({
        parentPackagingTypeId: packaging.parentPackagingTypeId,
        packagingTypeId: packaging.packagingTypeId,
        showChildDropdown: packaging.showChildDropdown,
        boxWeight: packaging.boxWeight,
        boxWidth: packaging.boxWidth,
        boxLength: packaging.boxLength,
        boxHeight: packaging.boxHeight,
        productLines: [],
      }),
    [
      packaging.parentPackagingTypeId,
      packaging.packagingTypeId,
      packaging.showChildDropdown,
      packaging.boxWeight,
      packaging.boxWidth,
      packaging.boxLength,
      packaging.boxHeight,
    ]
  );

  const addressComplete = useMemo(
    () => isParcelAddressStepComplete(destination) && !!hasMerchant,
    [destination, hasMerchant]
  );

  const { enabled, hints } = useMemo(
    () =>
      getParcelCreateStepAccess({
        addressComplete,
        productsComplete,
        packagingComplete,
      }),
    [addressComplete, productsComplete, packagingComplete]
  );

  return {
    enabled,
    hints,
    productsComplete,
    packagingComplete,
    addressComplete,
  };
}
